import Box from "@mui/material/Box";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";

import { renderProgress } from "../ProgressBar";
import { useState, useRef } from "react";
import env from "react-dotenv";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";

import { editDocument, processDocument } from "../../app/actions/documents";
import ChatModal from "./ChatModal";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const confidenceComparator = (v1, v2) => {
  return v1 - v2;
};

const imageDataColumns = [
  { headerName: "Description", field: "name", width: 150, editable: true },
  {
    headerName: "Confidence",
    field: "confidence",
    width: 150,
    renderCell: renderProgress,
    sortComparator: confidenceComparator,
  },
  { headerName: "Categories", field: "categories", width: 450 },
];

const wordDataColumns = [
  { headerName: "Word", field: "value", width: 150, editable: true },
  {
    headerName: "Confidence",
    field: "confidence",
    width: 150,
    renderCell: renderProgress,
    sortComparator: confidenceComparator,
  },
];

const azureDataColumns = [
  { headerName: "Field Label", field: "label", width: 150, editable: true },
  { headerName: "Field Value", field: "value", width: 150, editable: true },
  {
    headerName: "Confidence",
    field: "confidence",
    width: 150,
    renderCell: renderProgress,
    sortComparator: confidenceComparator,
  },
];

const defaultSorting = {
  sorting: {
    sortModel: [{ field: "confidence", sort: "desc" }],
  },
};

const imageTypes = ["image/jpeg", "image/png", "image/gif"];

const DocumentData = ({ file }) => {
  const { azure_data } = file;
  const [azureData, setAzureData] = useState(azure_data);
  const {
    words = [],
    key_values = [],
    image_data = [],
    tables = [],
  } = azureData;
  const [loading, setLoading] = useState(false);
  const [imageDataRows, setImageDataRows] = useState(() => {
    if (image_data.length) {
      const data = image_data.map((item, id) => {
        return {
          id,
          name: item.Name,
          confidence: item.Confidence.toFixed(2),
          categories: item.Categories.map((i) => {
            return i.Name;
          }).join(","),
        };
      });
      return data;
    } else {
      return false;
    }
  });
  const [selectedImageData, setSelectedImageData] = useState([]);

  const handleImageDataRowUpdate = (updatedRow, originalRow) => {
    // Find the index of the row that was edited
    const rowIndex = imageDataRows.findIndex((row) => row.id === updatedRow.id);

    // // Replace the old row with the updated row
    const updatedRows = [...imageDataRows];
    updatedRows[rowIndex] = updatedRow;

    // // Update the state with the new rows
    setImageDataRows(updatedRows);

    const rowIndexSelected = selectedImageData.findIndex(
      (row) => row.id === updatedRow.id
    );

    if (rowIndexSelected !== -1) {
      const updatedSelectedRows = [...selectedImageData];
      updatedSelectedRows[rowIndexSelected] = {
        id: updatedRow.id,
        value: `${updatedRow.label} ${updatedRow.value}`,
      };

      setSelectedImageData(updatedSelectedRows);
    }
    // Return the updated row to update the internal state of the DataGrid
    return updatedRow;
  };

  function EditImageDataToolbar(props) {
    const handleClick = () => {
      setImageDataRows((oldRows) => {
        const id = Math.max(...oldRows.map((o) => o.id), 0) + 1;
        return [...oldRows, { id, name: "", categories: "", confidence: 100 }];
      });
    };

    return (
      <GridToolbarContainer>
        <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
          Add record
        </Button>
        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    );
  }

  const [wordRows, setWordRows] = useState(() => {
    if (words.length) {
      const data = words.map((item, id) => {
        const confidence = item.confidence * 100;

        return {
          id,
          value: item.value,
          confidence: confidence.toFixed(2),
        };
      });
      return data;
    } else {
      return false;
    }
  });

  function EditWordToolbar(props) {
    return (
      <GridToolbarContainer>
        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    );
  }

  const [keyValuesRows, setKeyValuesRows] = useState(() => {
    if (key_values.length) {
      const data = key_values.map((item, id) => {
        const confidence = item.confidence * 100;

        return {
          id,
          label: item.key,
          value: item.value,
          confidence: confidence.toFixed(2),
        };
      });
      return data;
    } else {
      return false;
    }
  });
  const [selectedDocumentData, setSelectedDocumentData] = useState([]);

  const processFile = () => {
    setLoading(true);
    dispatch(processDocument(file.id))
      .then((data) => {
        const { azure_data: updatedData } = data;
        console.log(updatedData, data);
        setAzureData(updatedData);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };
  const handleKeyValuesRowUpdate = (updatedRow, originalRow) => {
    // Find the index of the row that was edited
    const rowIndex = keyValuesRows.findIndex((row) => row.id === updatedRow.id);

    // // Replace the old row with the updated row
    const updatedRows = [...keyValuesRows];
    updatedRows[rowIndex] = updatedRow;

    // // Update the state with the new rows
    setKeyValuesRows(updatedRows);

    const rowIndexSelected = selectedDocumentData.findIndex(
      (row) => row.id === updatedRow.id
    );

    if (rowIndexSelected !== -1) {
      const updatedSelectedRows = [...selectedDocumentData];
      updatedSelectedRows[rowIndexSelected] = {
        id: updatedRow.id,
        value: `${updatedRow.label} ${updatedRow.value}`,
      };

      setSelectedDocumentData(updatedSelectedRows);
    }
    // Return the updated row to update the internal state of the DataGrid
    return updatedRow;
  };

  function EditKeyValueToolbar(props) {
    const handleClick = () => {
      setKeyValuesRows((oldRows) => {
        const id = Math.max(...oldRows.map((o) => o.id), 0) + 1;
        return [...oldRows, { id, label: "", value: "", confidence: 100 }];
      });
    };

    return (
      <GridToolbarContainer>
        <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
          Add record
        </Button>
        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    );
  }

  const [tablesData, setTablesData] = useState(() => {
    if (tables.length) {
      const data = tables.map((table, id) => {
        const columns = [];
        const rows = [];

        table.forEach((item) => {
          if (item.kind === "columnHeader") {
            // Add column content to columns array
            columns.push({
              headerName: item.content,
              field: item.content,
              editable: true,
              minWidth: 100,
            });
          } else if (item.kind === "content") {
            // Group content objects by row_index
            if (rows[item.row_index] === undefined) {
              rows[item.row_index] = { id: item.row_index };
            }

            if (columns[item.column_index] !== undefined) {
              rows[item.row_index][columns[item.column_index].field] =
                item.content;
            } else {
              rows[item.row_index]["content"] = item.content;
              // rows[item.row_index].push(item.content);
            }
          }
        });

        return {
          columns,
          rows: rows.filter((n) => n),
        };
      });
      return data;
    } else {
      return false;
    }
  });

  const [addKeywords, setKeywords] = useState([]);

  let navigate = useNavigate();

  const dispatch = useDispatch();

  const handleSubmit = () => {
    const keywords = [].concat(
      addKeywords,
      selectedImageData.map((i) => i.value),
      selectedDocumentData.map((i) => i.value)
    );

    dispatch(editDocument(file.id, { keywords }))
      .then(() => {
        navigate("/my-documents");
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const filePath = `${env.UPLOADS_URL}/uploads/${file.name}`;

  return (
    <Box sx={{ width: "100%" }}>
      {Object.keys(azureData).length !== 0 ? (
        <ChatModal documentId={file.id} />
      ) : (
        <LoadingButton
          color="primary"
          variant="contained"
          loading={loading}
          loadingIndicator="Loading…"
          onClick={processFile}
        >
          Process Document
        </LoadingButton>
      )}

      <Grid container spacing={2}>
        <Grid item md={12}>
          {keyValuesRows && (
            <Accordion>
              <AccordionSummary
                expandIcon={<ArrowDownwardIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <h1> Labels </h1>
              </AccordionSummary>
              <AccordionDetails>
                <div style={{ height: 800, width: "100%" }}>
                  <DataGrid
                    initialState={defaultSorting}
                    key="keyValues"
                    rows={keyValuesRows}
                    columns={azureDataColumns}
                    processRowUpdate={handleKeyValuesRowUpdate}
                    onProcessRowUpdateError={(error) => console.log(error)}
                    checkboxSelection
                    disableRowSelectionOnClick
                    onRowSelectionModelChange={(selectionModel) => {
                      const selectedRowsData = selectionModel.map((id) => {
                        const item = keyValuesRows.find((row) => row.id === id);
                        return { id, value: `${item.label} ${item.value}` };
                      });
                      setSelectedDocumentData(selectedRowsData);
                    }}
                    slots={{
                      toolbar: EditKeyValueToolbar,
                    }}
                  />
                </div>
              </AccordionDetails>
            </Accordion>
          )}

          {imageDataRows && (
            <Accordion>
              <AccordionSummary
                expandIcon={<ArrowDownwardIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <h1> Image Items </h1>
              </AccordionSummary>
              <AccordionDetails>
                <DataGrid
                  initialState={defaultSorting}
                  key="imageData"
                  rows={imageDataRows}
                  columns={imageDataColumns}
                  processRowUpdate={handleImageDataRowUpdate}
                  onProcessRowUpdateError={(error) => console.log(error)}
                  disableRowSelectionOnClick
                  onRowSelectionModelChange={(selectionModel) => {
                    const selectedRowsData = selectionModel.map((id) => {
                      const item = imageDataRows.find((row) => row.id === id);
                      return { id, value: item.name };
                    });
                    setSelectedImageData(selectedRowsData);
                  }}
                  slots={{
                    toolbar: EditImageDataToolbar,
                  }}
                  checkboxSelection
                />
              </AccordionDetails>
            </Accordion>
          )}

          {tablesData && (
            <Accordion>
              <AccordionSummary
                expandIcon={<ArrowDownwardIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <h1>Table Items</h1>
              </AccordionSummary>
              <AccordionDetails>
                {tablesData.map(
                  (table, index) =>
                    table.columns.length > 0 && (
                      <div
                        style={{ marginBottom: 50, height: 500, width: "100%" }}
                      >
                        <DataGrid
                          key={index}
                          rows={table.rows}
                          columns={table.columns}
                          getRowSpacing={() => "auto"}
                          slots={{
                            toolbar: EditWordToolbar,
                          }}
                        />
                      </div>
                    )
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {wordRows && (
            <Accordion>
              <AccordionSummary
                expandIcon={<ArrowDownwardIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <h1>Words</h1>
              </AccordionSummary>
              <AccordionDetails>
                <div style={{ marginBottom: 50, height: 500, width: "100%" }}>
                  <DataGrid
                    initialState={defaultSorting}
                    rows={wordRows}
                    columns={wordDataColumns}
                    slots={{
                      toolbar: EditWordToolbar,
                    }}
                  />
                </div>
              </AccordionDetails>
            </Accordion>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentData;
